<template>
    <div>
        <config-section
            :title="$t('email_signatures.title')"
            :description="$t('email_signatures.description')"
        >

            <loading
                v-if="fetchingSignatures && !signatures.length"
                size="medium"
                align="left"
                class="pt-24 pl-16 justify-start"
            ></loading>

            <ul v-else-if="signatures.length" class="mt-8 rounded-6 overflow-hidden border border-primary-20">
                <li
                    v-for="signature in signatures"
                    :key="'signature-' + signature.id"
                    class="bg-white odd:bg-primary-5"
                >
                    <div class="flex items-center justify-between px-12 py-8">
                        <span class="truncate">{{ signature.name }}</span>
                        <div class="flex gap-8">
                            <simple-button
                                icon="edit"
                                small
                                @click="editingSignature = signature"
                            >
                                {{ $t('email_signatures.edit') }}
                            </simple-button>
                            <simple-button
                                display="danger"
                                icon="delete"
                                look="icon"
                                small
                                @click="deletingSignature = signature"
                            />
                        </div>
                    </div>
                </li>
            </ul>

            <div v-else-if="!creatingSignature" class="text-body-sm pt-8">
                {{ $t('email_signatures.no_signatures') }}
            </div>

            <template #buttons v-if="!fetchingSignatures || signatures.length">
                <simple-button
                    display="secondary"
                    look="primary"
                    @click="creatingSignature = true"
                >{{ signatures.length ? $t('email_signatures.add_new') : $t('email_signatures.add_first') }}</simple-button>
            </template>

        </config-section>

        <popup-modal-confirm
            v-if="deletingSignature"
            :message="$t('general.confirm_delete_subject', { subject: $t('email_signatures.named_signature', { name: deletingSignature.name }) })"
            :confirmText="$t('general.delete_subject', { subject: $t('email_signatures.signature') })"
            :working="performingDeleteRequest"
            show-exclamation
            @confirm="deleteSignature"
            @close="deletingSignature = null"
        >
        </popup-modal-confirm>

        <signature-editor
            v-if="creatingSignature"
            @close="creatingSignature = false"
            :title="$t('email_signatures.create_signature')"
        ></signature-editor>

        <signature-editor
            v-if="editingSignature"
            :signature="editingSignature"
            @close="editingSignature = null"
            :title="$t('email_signatures.edit_signature')"
        ></signature-editor>
    </div>
</template>

<script>
import ConfigurationSection from './ConfigurationSection.vue';
import SimpleButton from './ui/SimpleButton.vue';
import * as signatures from '../services/EmailSignatureService';
import EmailSignature from '../models/EmailSignature';
import { normalizeJson } from '../../shared/helpers';
import { mapState } from 'pinia';
import {useRepo} from 'pinia-orm';
import {useAuthenticationStore} from '../state/modules/authentication';
import SvgIcon from './ui/SvgIcon.vue';
import PopupModalConfirm from './ui/PopupModalConfirm.vue';
import Loading from './ui/Loading.vue';
import EmailSignatureEditor from './EmailSignatureEditor.vue';

export default {
    components: {
        'config-section': ConfigurationSection,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'popup-modal-confirm': PopupModalConfirm,
        'loading': Loading,
        'signature-editor': EmailSignatureEditor,
    },
    data: function () {
        return {
            fetchingSignatures: true,
            creatingSignature: false,
            editingSignature: null,
            deletingSignature: null,
            performingDeleteRequest: false,
        }
    },
    computed: {
        ...mapState(useAuthenticationStore, ['currentUserId']),
        signatures () {
            return useRepo(EmailSignature)
                .with('associatedCompanies')
                .orderBy('created_at', 'asc')
                .where('user_id', this.currentUserId)
                .get();
        }
    },
    methods: {
        deleteSignature () {

            this.performingDeleteRequest = true;

            signatures.destroy(this.deletingSignature.id).then(() => {
                useRepo(EmailSignature).destroy(this.deletingSignature.id);
                this.deletingSignature = null;
                this.performingDeleteRequest = false;
            });
        }
    },
    mounted () {
        signatures.index().then(result => {
            return useRepo(EmailSignature).save(normalizeJson(result.data));
        }).then(() => {
            this.fetchingSignatures = false;
        });
    }
}
</script>
